import React, { useState, useRef, useEffect } from "react";
import {
    Box,
    Avatar,
    IconButton,
    Typography,
    Paper,
    Fade,
    Button,
    Divider,
    Badge,
    Tooltip,
    useTheme,
    ClickAwayListener,
    useMediaQuery,
    Dialog,
    DialogTitle,
    DialogContent,
    Stack,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import EventNoteIcon from "@mui/icons-material/EventNote";
import InfoIcon from "@mui/icons-material/Info";
import CampaignIcon from "@mui/icons-material/Campaign";
import { useAuth } from "../../hooks/useAuth";
import { AllNoticeList, GetNoticeCategorylist } from "../../api/auth/authService";
import BookOutlinedIcon from '@mui/icons-material/BookOutlined';
import InfoOutlineIcon from '@mui/icons-material/InfoOutline';
import CloseIcon from '@mui/icons-material/Close';
import { useSnackbar } from '../MUIComponent/FeedbackComponent/SnackBar/SnackbarContext';
import { useDispatch, useSelector } from "react-redux";
import { setNotifications, markAllAsRead, setHasUnread } from "../../features/notifications/notificationSlice";
import ReusableModal from "../MUIComponent/Utlis/Modal/Modal";
import dayjs from "dayjs";
import isBetween from "dayjs/plugin/isBetween";
dayjs.extend(isBetween);
export default function NotificationDropdown() {

    const [open, setOpen] = useState(false);
    const bellRef = useRef(null);
    const customColors = useSelector(state => state.theme.customColors);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    // const [notifications, setNotifications] = useState([]);
    const { user } = useAuth();
    const today = dayjs();
    const [dialogOpen, setDialogOpen] = useState(false);
    const menuState = useSelector((state) => state.menu);
    const { showSnackbar } = useSnackbar();
    const ActiveAcademicYears = useSelector(state => state.academicYear.academicYear);
    const dispatch = useDispatch();
    const { hasUnread, notifications } = useSelector((state) => state.notification);
    const NotificationBox = styled(Paper)(({ theme }) => ({
        position: "absolute",
        top: 50,
        right: isMobile ? '-55px' : "20px",
        width: 360,
        [theme.breakpoints.down("md")]: {
            width: 360,
            transform: 'translateX(15%)',
            right: 0
        },

        [theme.breakpoints.down("sm")]: {
            width: 300,
            transform: 'translateX(15%)',
            right: 0,
            top: 48,
        },
        maxHeight: 500,
        overflowY: "auto",
        borderRadius: 8,
        // boxShadow: theme.shadows[4],
        zIndex: 999,
        boxShadow: '0px 8px 12px rgba(0, 0, 0, 0.1)',
        scrollbarWidth: "thin",
        scrollbarColor: "rgba(193, 193, 193, 0.3) transparent",
        "&::-webkit-scrollbar": { width: "1px" },
        "&::-webkit-scrollbar-track": { background: "transparent" },
        "&::-webkit-scrollbar-thumb": {
            backgroundColor: "rgba(193, 193, 193, 0.3)",
            borderRadius: "10px",
            border: "none",
        },
        "&::-webkit-scrollbar-thumb:hover": {
            backgroundColor: "rgba(160, 160, 160, 0.5)",
        },
    }));

    // const handleViewAll = () => setDialogOpen(true);
    const handleViewAll = () => {
        setDialogOpen(true);
        setOpen(false);
        dispatch(markAllAsRead());
        const readIds = notifications.map(n => n.id);
        const userKey =
            user?.adminID || user?.employeeID || "default";
        localStorage.setItem(`readNotifications_${userKey}`, JSON.stringify(readIds));
    };

    const darkThemeCategoryColors = {
        academic: theme.palette.background.noticebg1,
        "upcoming events": theme.palette.background.eventCardbg,
        alerts: theme.palette.background.alertCardbg,
        general: theme.palette.background.noticeCardbg,
    };

    const categoryIconMap = {
        academic: <i class="fa-sharp fa-regular fa-id-card" style={{ color: theme.palette.background.noticeIconbg1, fontSize: '18px' }}></i>,
        "upcoming events": <i class="fa-sharp fa-solid fa-calendar-lines" style={{ color: theme.palette.success.main, fontSize: '18px' }}></i>,
        general: <i class="fa-sharp fa-regular fa-circle-info" style={{ color: theme.palette.background.noticeIconbg2, fontSize: '18px' }}></i>,
        alerts: <i class="fa-sharp fa-regular fa-bullhorn" style={{ color: theme.palette.custom.alertIcon, fontSize: '18px' }}></i>,
    };

    const getNotificationColor = (type, apiColor) => {
        return theme.palette.mode === "dark"
            ? darkThemeCategoryColors[type] || "#1a1a1a"
            : apiColor || "#f5f5f5";
    };

    const NotificationItem = ({ title, message, time, type, apiColor }) => {
        const color = getNotificationColor(type, apiColor);

        return (
            <Box
                px={1.5}
                py={1}
                display="flex"
                gap={1.5}
                alignItems="flex-start"
                bgcolor={color}
                borderRadius={2}
                mx={0.5}
                my={1}
            >
                <Box mt={0.5}>
                    {categoryIconMap[type] || <InfoIcon sx={{ color: theme.palette.text.secondary }} />}
                </Box>
                <Box flex={1}>
                    <Typography fontWeight="600" fontSize="13px" lineHeight={1.2} sx={{ wordBreak: 'break-word' }}>
                        {title}
                    </Typography>
                    <Typography
                        variant="body2"
                        sx={{
                            fontSize: "12px",
                            color: "text.secondary",
                            lineHeight: 1.2,
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            wordBreak: 'break-word'
                        }}
                    >
                        {message}
                    </Typography>
                    <Typography variant="caption" sx={{ fontSize: "11px", color: "text.disabled" }}>
                        {time}
                    </Typography>
                </Box>
            </Box>
        );
    };

    const fetchNotifications = async () => {
        try {
            if (!user?.schoolID) return;

            const [noticeRes, categoryRes] = await Promise.all([
                AllNoticeList(user?.schoolID, ActiveAcademicYears?.academicYearID),
                GetNoticeCategorylist(),
            ]);

            if (noticeRes.success && categoryRes.success) {
                const categories = categoryRes.data.map(c => ({
                    ...c,
                    key: c.categoryName.trim().toLowerCase(),
                }));

                const userKey = user?.adminID || user?.employeeID || "default";
                const readIds = JSON.parse(localStorage.getItem(`readNotifications_${userKey}`) || "[]");
                const notices = noticeRes?.data?.notices || [];
                const activeNotices = notices.filter(n => {
                    if (!n.isActive) return false;
                    if (!n.startDate || !n.endDate) return false;

                    const start = dayjs(n.startDate);
                    const end = dayjs(n.endDate);

                    return today.isBetween(start, end, 'day', '[]');
                });

                const groupedNoticesMap = new Map();

                activeNotices.forEach(notice => {
                    const groupKey = `${notice.noticeTitle.trim().toLowerCase()}_${notice.noticeDescription.trim().toLowerCase()}_${notice.schoolId}`;
                    if (!groupedNoticesMap.has(groupKey)) {
                        groupedNoticesMap.set(groupKey, notice);
                    }
                });

                const groupedNotices = Array.from(groupedNoticesMap.values());
                const transformed = (groupedNotices || []).map(notice => {
                    const catName = notice.categoryName?.replace(/\r?\n/g, "")?.trim()?.toLowerCase();
                    const category = categories.find(c => c.key === catName);

                    return {
                        id: notice.noticeId,
                        title: notice.noticeTitle,
                        message: notice.noticeDescription,
                        time: new Date(notice.noticeDate).toLocaleDateString("en-GB"),
                        type: catName,
                        apiColor: category?.colorCode,
                        read: readIds.includes(notice.noticeId),
                    };
                });

                dispatch(setNotifications(transformed));

                const newOnes = transformed.filter(n => !n.read);
                dispatch(setHasUnread(newOnes.length > 0));
            } else {
                dispatch(setNotifications([]));
                dispatch(setHasUnread(false));
            }
        } catch (error) {
            dispatch(setNotifications([]));
            dispatch(setHasUnread(false));
        }
    };


    useEffect(() => {
        fetchNotifications();
    }, [user?.schoolID, menuState.lastUpdated, ActiveAcademicYears?.academicYearID]);

    return (
        <>
            <Box position="relative">
                <Tooltip title="Notification">
                    <IconButton
                        ref={bellRef}
                        onClick={() => setOpen(!open)}
                        className="head-icon-btn"
                        sx={{
                            color: theme.palette.background.text,
                            borderColor: theme.palette.custom?.borderColor,
                        }}
                    >
                        <Badge color="primary" variant="dot" invisible={!hasUnread}>
                            {/* <IconBell stroke={2} size={16} className="swing" /> */}
                            <i class="fa-sharp fa-regular fa-bell"></i>
                        </Badge>
                    </IconButton>
                </Tooltip>
                {open && (
                    <ClickAwayListener onClickAway={() => setOpen(false)}>
                        <NotificationBox>
                            <Box px={2} py={1}>
                                <Typography variant="subtitle1" fontWeight="bold">
                                    Notification
                                </Typography>
                            </Box>

                            <Box
                                sx={{
                                    maxHeight: 400,
                                    overflowY: 'auto',
                                    px: 0.5,
                                    pb: 1,
                                    scrollbarWidth: 'thin',
                                    '&::-webkit-scrollbar': { width: '1px' },
                                    '&::-webkit-scrollbar-thumb': {
                                        backgroundColor: '#c1c1c1',
                                        borderRadius: '8px',
                                    },
                                }}
                            >
                                {notifications.map((n, i) => (
                                    <NotificationItem key={i} {...n} />
                                ))}
                            </Box>

                            {notifications.length > 0 && (
                                <Box textAlign="center" py={1} sx={{ position: 'sticky', bottom: 0, bgcolor: 'background.paper' }} onClick={(e) => e.stopPropagation()}>
                                    <Button size="small" onClick={handleViewAll} sx={{
                                        color: theme.palette.primary.main
                                    }}>
                                        View all notifications
                                    </Button>
                                </Box>
                            )}
                        </NotificationBox>
                    </ClickAwayListener>
                )}
            </Box>
            <ReusableModal
                open={dialogOpen} onClose={() => setDialogOpen(false)}
                title='All Notifications'
            >
                <Box className="voilet-scroll-container" sx={{ maxHeight: '470px', overflowY: 'auto' }}>
                    <Stack spacing={2}>
                        {notifications.map((item) => {
                            const color = getNotificationColor(item.type, item.apiColor);
                            return (
                                <Box
                                    key={item.id}
                                    sx={{
                                        p: 1.5,
                                        backgroundColor: color,
                                        borderRadius: '8px',
                                        display: 'flex',
                                        flexDirection: { xs: 'column', sm: 'row' },
                                        alignItems: 'flex-start',
                                        gap: 1.5,
                                    }}
                                >
                                    {categoryIconMap[item.type]}
                                    <Box flex={1}>
                                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                                            <Typography fontWeight={600} color={theme.palette.custom.tableHeadingText}>{item.title}</Typography>
                                            <Typography variant="caption" color="text.disabled">{item.time}</Typography>
                                        </Stack>
                                        <Typography variant="body2" color="text.secondary" mt={1}>{item.message}</Typography>
                                    </Box>
                                </Box>
                            );
                        })}
                    </Stack>
                </Box>
            </ReusableModal>
            {/* <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="md">
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    All Notifications
                    <IconButton onClick={() => setDialogOpen(false)} size="small">
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers>
                    <Stack spacing={2}>
                        {notifications.map((item) => {
                            const color = getNotificationColor(item.type, item.apiColor);
                            return (
                                <Box
                                    key={item.id}
                                    sx={{
                                        p: 1.5,
                                        backgroundColor: color,
                                        borderRadius: '8px',
                                        display: 'flex',
                                        alignItems: 'flex-start',
                                        gap: 1.5,
                                    }}
                                >
                                    {categoryIconMap[item.type]}
                                    <Box flex={1}>
                                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                                            <Typography fontWeight={600}>{item.title}</Typography>
                                            <Typography variant="caption" color="text.disabled">{item.time}</Typography>
                                        </Stack>
                                        <Typography variant="body2" color="text.secondary" mt={1}>{item.message}</Typography>
                                    </Box>
                                </Box>
                            );
                        })}
                    </Stack>
                </DialogContent>
            </Dialog> */}
        </>
    );
}
